/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Table Fixed Columns Sticky
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import type { CSSProperties, RefObject } from 'react';

import classNames from 'classnames';
import debounce from 'lodash/debounce';
import InfiniteScroll from 'react-infinite-scroller';
import { connect, useDispatch } from 'react-redux';
import { useBlockLayout, useExpanded, useResizeColumns, useTable } from 'react-table';
import type { Row } from 'react-table';
import { VariableSizeList } from 'react-window';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';

import { getIsFastActionPage } from '@/main/components/Table/utils/tableUtils';
import { LinkBlock } from '@components/common';
import { tableRowRestoreAction } from '@components/Table/actions/tableActions';
import { TABLE_WIDTH_MAX } from '@components/Table/constants';
import { TableLoaderContainerStyled, TableRemovedRowItemStyled } from '@components/Table/styled/TableStyled';
import type { ITableProps } from '@components/Table/Table';
import { EColumnCellType } from '@components/Table/types/columnTypes';
import './styles.css';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import type { ICommonTranslation } from '@modules/internationalization/types';
import { useResizeObserver } from '@modules/table/hooks/useTableResizer';
import type { IRootReducer } from '@reducers/rootReducer';

import {
    findNextColumnNotHidden,
    findPrevColumnNotHidden,
    IFixedColumn,
    IFixedColumns,
    isLeftFixed,
    isRightFixed,
} from './helpers';

interface IColumnWithFixed {
    className: string;
    headerClassName: string;
    style: {
        flexBasis?: number | string;
        flexGrow: number;
        flexShrink: number;
        left: false | number;
        width?: number | string;
    };
}

interface IDefaultColumn {
    width: number;
}

const Styles = styled.div`
    .infiniti-scroll {
        overflow: unset !important;
    }

    .window-scroller-override {
        height: 100% !important;
        overflow: unset !important;
    }
`;

const getLeftOffsetColumns = (columns: IFixedColumns, index: number): number => {
    let offset = 0;
    for (let i = 0; i < index; i += 1) {
        const column = columns[i];
        if (column.show !== false) {
            const width: number = (typeof column.width === 'string' ? parseInt(column.width) : column.width) || 100;
            offset += Math.min(width, TABLE_WIDTH_MAX);
        }
    }

    return offset;
};

const getColumnWithFixed = (
    column: IFixedColumn,
    columns: any,
    index: number,
    columnsResizeMap: Map<string, number>,
    isLoading: boolean,
): IColumnWithFixed => {
    const fixed = column.fixed || false;

    const nextColumn = findNextColumnNotHidden(columns, index);
    const parentIsLastFixedInner = fixed && (!nextColumn || (nextColumn && !nextColumn.fixed));
    const isLastFixed = fixed && (!nextColumn || (nextColumn && !nextColumn.fixed));

    const prevColumn = findPrevColumnNotHidden(columns, index);
    const parentIsFirstFixedInner = fixed && (!prevColumn || (prevColumn && !prevColumn.fixed));
    const isFirstFixed = fixed && (!prevColumn || (prevColumn && !prevColumn.fixed));

    const columnIsLeftFixed = isLeftFixed({ fixed });
    const columnIsRightFixed = isRightFixed({ fixed });

    const left: false | number = columnIsLeftFixed && getLeftOffsetColumns(columns, index);

    const className = classNames(column.className, {
        'rthfc-td-fixed': fixed,
        'rthfc-td-fixed-left': columnIsLeftFixed,
        'rthfc-td-fixed-left-last': isLastFixed,
        'rthfc-td-fixed-right': columnIsRightFixed,
        'rthfc-td-fixed-right-first': isFirstFixed,
    });
    const headerClassName = classNames(column.headerClassName, {
        'rthfc-th-fixed': fixed,
        'rthfc-th-fixed-left': columnIsLeftFixed,
        'rthfc-th-fixed-left-last': parentIsLastFixedInner,
        'rthfc-th-fixed-right': columnIsRightFixed,
        'rthfc-th-fixed-right-first': parentIsFirstFixedInner,
    });

    let columnWidth = column.width;

    if (
        isLoading &&
        column.canResize &&
        !column.isResizing &&
        columnsResizeMap.size &&
        column.id &&
        columnsResizeMap.has(column.id)
    ) {
        const columnResizeDataWidth = columnsResizeMap.get(column.id);

        if (column.width !== columnResizeDataWidth) {
            columnWidth = columnResizeDataWidth;
        }
    }

    const style = {
        flexBasis: column.width,
        flexShrink: 0,
        flexGrow: 1,
        left,
        width: columnWidth,
    };
    return {
        className,
        headerClassName,
        style,
    };
};

let isLoading = false;

const TableFixedColumnsSticky = ({
    columns,
    counter,
    data,
    footerHeight,
    isLoading: propsIsLoading,
    isTableFooter,
    innerRef,
    onResizedChange,
    onScrollEnd,
    removedRowIndexes,
    rowDataName,
    rowHeight,
    tableDataName,
    translation,
}: ITableProps & IConnectedProps) => {
    const virtualRef: RefObject<VariableSizeList> = useRef<VariableSizeList>(null);
    const tableBodyRef = useRef<HTMLDivElement>(null);
    const columnResizeRef = useRef<Map<string, number>>(new Map());
    
    const isFastActionPage = getIsFastActionPage();

    const defaultColumn: IDefaultColumn = useMemo(
        (): IDefaultColumn => ({
            width: 140,
        }),
        [],
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, totalColumnsWidth, prepareRow, state } = useTable(
        // @ts-ignore
        { autoResetExpanded: false, columns, data, defaultColumn },
        useBlockLayout,
        useExpanded,
        useResizeColumns,
    );

    useEffect(() => {
        virtualRef.current?.resetAfterIndex(0, true);
        isLoading = false;
    }, [data]);

    useEffect(() => {
        window.addEventListener('scroll', windowScrollHandler, true);
        return () => {
            window.removeEventListener('scroll', windowScrollHandler, true);
        };
    }, []);

    const windowScrollHandler = (): void => {
        const tableBodyScrollTop = tableBodyRef?.current?.getBoundingClientRect().top ?? 0;
        if (tableBodyScrollTop < 0) {
            virtualRef.current?.scrollTo(Math.abs(tableBodyScrollTop));
        } else {
            virtualRef.current?.scrollTo(0);
        }
    };

    const dispatch = useDispatch();

    const isRowRemoved = useCallback((index: number) => removedRowIndexes?.includes(index), [removedRowIndexes]);

    const RenderRow = useCallback(
        ({ index, style }: { index: number; style: CSSProperties }) => {
            // react-table is v7 but @types/react-table only 6.8.5 version
            //eslint-disable-next-line @typescript-eslint/no-explicit-any
            const row: Row<any> = rows[index];
            prepareRow(row);

            const checked = isFastActionPage ? row.original.fastActionChecked : row.original.checked;

            const rowClassName = classNames('rt-tr', {
                'rt-tr-checked': checked ?? false,
                'rt-tr-delete': isRowRemoved(index) ?? false,
            });
            const rowData = rowDataName ? row.original[rowDataName] ?? '' : '';
            return (
                <div className="rt-tr-group">
                    <div
                        {...row.getRowProps({
                            style,
                        })}
                        className={rowClassName}
                        data-row-name={rowData}
                    >
                        {row.cells.map((cell: any, index: number) => {
                            const { className, style } = getColumnWithFixed(
                                cell.column,
                                headerGroups[0].headers,
                                index,
                                columnResizeRef.current,
                                propsIsLoading,
                            ); // @ts-ignore

                            const minWidth = cell.column.minResizeWidth ?? 0;
                            const styleWidth = style.width ?? 0;
                            const width =
                                styleWidth > TABLE_WIDTH_MAX
                                    ? TABLE_WIDTH_MAX
                                    : styleWidth < minWidth
                                    ? minWidth
                                    : styleWidth;
                            const cellStyle = {
                                ...style,
                                flexBasis: width,
                                maxWidth: width,
                                minWidth,
                                width,
                            };
                            const blockClasses = classNames('rt-td', className);
                            return (
                                <div
                                    key={cell.value}
                                    {...cell.getCellProps()}
                                    className={blockClasses}
                                    data-col-name={cell.column.id}
                                    data-name={`${cell.column.id}-${index}`}
                                    style={cellStyle}
                                >
                                    {cell.render('Cell')}
                                </div>
                            );
                        })}
                        {isRowRemoved(index) && (
                            <TableRemovedRowItemStyled>
                                <LinkBlock
                                    dataName="table-row-restore"
                                    onClick={() => dispatch(tableRowRestoreAction({ index: index }))}
                                >
                                    {translation.commonTable.restoreRow}
                                </LinkBlock>
                            </TableRemovedRowItemStyled>
                        )}
                    </div>
                </div>
            );
        },
        [
            dispatch,
            headerGroups,
            isRowRemoved,
            prepareRow,
            propsIsLoading,
            rowDataName,
            rows,
            translation.commonTable.restoreRow,
            isFastActionPage,
        ],
    );

    const getRowSize = (index: number) => {
        let height: any;
        const row: Row<any> = rows[index];
        prepareRow(row);

        const labels = row.original.labels ?? [];

        if (labels.length > 1) {
            height = rowHeight.default + (labels.length - 1) * rowHeight.next;
        } else {
            const doubleRow = row.cells.some(
                (cell) => ((cell.column as any)?.rowCount ?? '') === EColumnCellType.double,
            );
            height = doubleRow ? rowHeight.defaultDouble : rowHeight.default;
        }

        return height;
    };

    const loadMore = () => {
        if (!isLoading && onScrollEnd && data.length) {
            onScrollEnd();
            isLoading = true;
        }
    };

    const debounceLoadMore = debounce(loadMore, 100);

    useResizeObserver(state, (columnId, columnSize) => {
        onResizedChange([{ id: columnId, value: columnSize }]);
        columnResizeRef.current.set(columnId, columnSize);
    });

    return (
        <Styles ref={innerRef}>
            <div {...getTableProps()} className="ReactTable rthfc-fixed-columns rthfc -sp">
                <div className="rt-table" data-name={tableDataName} data-table-name={tableDataName}>
                    <div className="rt-thead -header">
                        {headerGroups.map((headerGroup) => (
                            <div
                                {...headerGroup.getHeaderGroupProps()} // key inside
                                className="tr rt-tr"
                            >
                                {headerGroup.headers.map((column: any, index: number) => {
                                    const { headerClassName, style } = getColumnWithFixed(
                                        column,
                                        headerGroup.headers,
                                        index,
                                        columnResizeRef.current,
                                        propsIsLoading,
                                    );
                                    const minWidth = column.minResizeWidth ?? 0;
                                    const styleWidth = style.width ?? 0;
                                    const width =
                                        styleWidth > TABLE_WIDTH_MAX
                                            ? TABLE_WIDTH_MAX
                                            : styleWidth < minWidth
                                            ? minWidth
                                            : styleWidth;
                                    const cellStyle = {
                                        ...style,
                                        flexBasis: width,
                                        maxWidth: width,
                                        minWidth,
                                        width,
                                        userSelect: 'auto',
                                    };
                                    const blockClasses = classNames('th', 'rt-th', headerClassName);
                                    return (
                                        <div
                                            key={column}
                                            {...column.getHeaderProps()}
                                            className={blockClasses}
                                            data-col-name={column.id}
                                            data-name={`${column.id}-header`}
                                            style={{ ...cellStyle }}
                                        >
                                            {column.render('Header')}
                                            {column.resizable && (
                                                <div
                                                    {...column.getResizerProps()}
                                                    className={`
                                                        resizer
                                                        rt-resizer
                                                        ${column.isResizing ? 'isResizing' : ''}`}
                                                    data-resizer-name={column.id}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                        {propsIsLoading && <TableLoaderContainerStyled isSticky style={{ width: totalColumnsWidth }} />}
                    </div>
                    <div {...getTableBodyProps()} className="rt-tbody" ref={tableBodyRef}>
                        <InfiniteScroll hasMore={rows.length < counter} loadMore={debounceLoadMore} pageStart={0}>
                            <Fragment>
                                <VariableSizeList
                                    className="window-scroller-override"
                                    height={window.innerHeight}
                                    itemCount={rows.length}
                                    itemSize={getRowSize}
                                    ref={virtualRef}
                                    width={totalColumnsWidth}
                                >
                                    {RenderRow}
                                </VariableSizeList>
                            </Fragment>
                        </InfiniteScroll>
                    </div>
                    <div className="rt-tfooter -footer" style={{ bottom: footerHeight ?? 33 }}>
                        {isTableFooter &&
                            headerGroups.map((headerGroup) => (
                                <div {...headerGroup.getHeaderGroupProps()} className="tr rt-tr" key={headerGroup.id}>
                                    {headerGroup.headers.map((column: any, index: number) => {
                                        const { className, style } = getColumnWithFixed(
                                            column,
                                            headerGroup.headers,
                                            index,
                                            columnResizeRef.current,
                                            propsIsLoading,
                                        );
                                        const minWidth = column?.minResizeWidth ?? 0;
                                        const styleWidth = style?.width ?? 0;
                                        const width =
                                            styleWidth > TABLE_WIDTH_MAX
                                                ? TABLE_WIDTH_MAX
                                                : styleWidth < minWidth
                                                ? minWidth
                                                : styleWidth;
                                        const cellStyle = {
                                            ...style,
                                            flexBasis: width,
                                            maxWidth: width,
                                            minWidth,
                                            width,
                                        };
                                        const blockClasses = classNames('td', 'rt-td', className);
                                        return (
                                            <div
                                                key={column}
                                                {...column.getHeaderProps()}
                                                className={blockClasses}
                                                data-col-name={column.id}
                                                data-name={`${column.id}-footer`}
                                                style={{ ...cellStyle }}
                                            >
                                                {column.render('Footer')}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </Styles>
    );
};

export interface IConnectedProps {
    translation: ICommonTranslation;
}

const mapStateToProps = () =>
    createStructuredSelector<IRootReducer, IConnectedProps>({
        translation: makeSelectTranslationCommon(),
    });

export default connect(mapStateToProps)(TableFixedColumnsSticky);
